import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { AVAILABLE_MODULES } from '../constants/modules';
import { Badge, EmptyState, InfoBanner } from '../components/ui';
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  PenTool
} from 'lucide-react';

const Roles = () => {
  const { isSuperAdmin, hasPermission, getAllRoles, getRoleById, addRole, updateRole, deleteRole } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPermissions, setEditPermissions] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: AVAILABLE_MODULES.reduce((acc, module) => {
      acc[module.id] = { read: false, write: false };
      return acc;
    }, {})
  });

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const allRoles = await getAllRoles();
      const rolesWithPerms = await Promise.all(
        allRoles.map(async (role) => {
          try {
            const detail = await getRoleById(role.id);
            const permsMap = {};
            if (detail?.permissions) {
              for (const p of detail.permissions) {
                permsMap[p.module_id] = { read: p.can_read, write: p.can_write };
              }
            }
            return { ...role, permissions: permsMap };
          } catch {
            return { ...role, permissions: {} };
          }
        })
      );
      setRoles(rolesWithPerms);
    } catch (err) {
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [getAllRoles, getRoleById]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: AVAILABLE_MODULES.reduce((acc, m) => { acc[m.id] = { read: false, write: false }; return acc; }, {})
    });
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handlePermissionChange = (moduleId, permissionType, checked, isEdit = false) => {
    const setter = isEdit ? setEditPermissions : (fn) => setFormData(prev => ({ ...prev, permissions: fn(prev.permissions) }));

    if (isEdit) {
      setter(prev => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [permissionType]: checked,
          read: permissionType === 'write' && checked ? true : (prev[moduleId]?.read ?? false)
        }
      }));
    } else {
      setter(prev => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [permissionType]: checked,
          read: permissionType === 'write' && checked ? true : (prev[moduleId]?.read ?? false)
        }
      }));
    }
  };

  const validateForm = (perms) => {
    if (!formData.name.trim()) {
      setError('Role name is required');
      return false;
    }
    const hasAny = Object.values(perms).some(p => p.read || p.write);
    if (!hasAny) {
      setError('At least one module permission must be granted');
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm(formData.permissions)) return;
    try {
      await addRole({ name: formData.name.trim(), description: formData.description.trim(), permissions: formData.permissions });
      resetForm();
      setShowAddForm(false);
      setSuccess('Role created successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadRoles();
    } catch (err) {
      setError(err.message || 'Failed to create role');
    }
  };

  const handleEdit = (role) => {
    setEditingId(role.id);
    setFormData({ name: role.name, description: role.description || '', permissions: {} });
    const perms = {};
    for (const m of AVAILABLE_MODULES) {
      perms[m.id] = role.permissions?.[m.id] || { read: false, write: false };
    }
    setEditPermissions(perms);
    setError('');
    setSuccess('');
  };

  const handleUpdate = async () => {
    if (!validateForm(editPermissions)) return;
    try {
      await updateRole(editingId, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: editPermissions
      });
      setEditingId(null);
      setSuccess('Role updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadRoles();
    } catch (err) {
      setError(err.message || 'Failed to update role');
    }
  };

  const handleDelete = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"? This cannot be undone.`)) return;
    try {
      await deleteRole(roleId);
      setSuccess('Role deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadRoles();
    } catch (err) {
      setError(err.message || 'Failed to delete role');
    }
  };

  const canWrite = isSuperAdmin || hasPermission('roles', 'write');

  if (!isSuperAdmin && !hasPermission('roles', 'read')) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto">
          <InfoBanner variant="danger" title="Access Denied">
            You don't have permission to view roles.
          </InfoBanner>
        </div>
      </DashboardLayout>
    );
  }

  const PermissionGrid = ({ perms, onChange, disabled = false }) => (
    <div className="bg-ga-bg-surface-subtle rounded-ga-md p-4 space-y-3">
      {AVAILABLE_MODULES.map((mod) => (
        <div key={mod.id} className="ga-card rounded-ga-sm p-3 border border-ga-border-subtle">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-medium text-ga-text-primary text-sm">{mod.name}</h4>
              <p className="text-xs text-ga-text-muted">{mod.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={perms[mod.id]?.read || false}
                onChange={(e) => onChange(mod.id, 'read', e.target.checked)}
                disabled={disabled}
                className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary disabled:opacity-50"
              />
              <div className="flex items-center space-x-1">
                <Eye className="h-4 w-4 text-ga-text-muted" />
                <span className="text-sm text-ga-text-primary">Read</span>
              </div>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={perms[mod.id]?.write || false}
                onChange={(e) => onChange(mod.id, 'write', e.target.checked)}
                disabled={disabled || !perms[mod.id]?.read}
                className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary disabled:opacity-50"
              />
              <div className="flex items-center space-x-1">
                <PenTool className="h-4 w-4 text-ga-text-muted" />
                <span className="text-sm text-ga-text-primary">Write</span>
              </div>
            </label>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ga-text-primary mb-2">Role Management</h1>
            <p className="text-ga-text-muted">Create and manage roles with module-level permissions</p>
          </div>
          {canWrite && (
            <button
              onClick={() => { resetForm(); setShowAddForm(true); setEditingId(null); }}
              className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
            >
              <Plus className="h-5 w-5" />
              <span>Create Role</span>
            </button>
          )}
        </div>

        {error && (
          <InfoBanner variant="danger" className="mb-4">{error}</InfoBanner>
        )}

        {success && (
          <InfoBanner variant="success" className="mb-4">{success}</InfoBanner>
        )}

        {showAddForm && canWrite && (
          <div className="mb-6 p-6 ga-card rounded-ga-md shadow-ga-soft border border-ga-border-subtle">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4">Create New Role</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Role Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="Enter role name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="2"
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="Enter role description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-3">Module Permissions *</label>
                <PermissionGrid perms={formData.permissions} onChange={(m, t, c) => handlePermissionChange(m, t, c, false)} />
              </div>
            </div>
            <div className="mt-4 flex space-x-2">
              <button onClick={handleAdd}
                className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md">
                <Save className="h-4 w-4" /><span>Create Role</span>
              </button>
              <button onClick={() => { setShowAddForm(false); resetForm(); }}
                className="ga-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-ga-md">
                <X className="h-4 w-4" /><span>Cancel</span>
              </button>
            </div>
          </div>
        )}

        <div className="ga-card rounded-ga-md shadow-ga-soft overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ga-primary mx-auto"></div>
              <p className="text-ga-text-muted mt-2">Loading roles...</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="ga-table min-w-full divide-y divide-ga-border-subtle">
              <thead className="bg-ga-bg-surface-subtle">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Permissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Type</th>
                  {canWrite && <th className="px-6 py-3 text-right text-xs font-medium text-ga-text-muted uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-ga-border-subtle">
                {roles.length === 0 ? (
                  <tr><td colSpan={canWrite ? 5 : 4} className="px-6 py-8">
                    <EmptyState
                      icon={Shield}
                      title="No roles found"
                      description="Create a role to define module-level permissions for your team."
                    />
                  </td></tr>
                ) : (
                  roles.map((role) => {
                    const perms = role.permissions || {};
                    const readCount = Object.values(perms).filter(p => p.read).length;
                    const writeCount = Object.values(perms).filter(p => p.write).length;

                    return (
                      <tr key={role.id} className="hover:bg-ga-bg-surface-subtle">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-ga-primary-soft flex items-center justify-center">
                              <Shield className="h-5 w-5 text-ga-primary" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-ga-text-primary">{role.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-ga-text-muted">{role.description || 'No description'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4 text-sm text-ga-text-primary">
                            <span className="flex items-center space-x-1">
                              <Eye className="h-4 w-4 text-ga-info" />
                              <span>{readCount} modules</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <PenTool className="h-4 w-4 text-ga-success" />
                              <span>{writeCount} modules</span>
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={role.is_system ? 'info' : 'neutral'}>
                            {role.is_system ? 'System' : 'Custom'}
                          </Badge>
                        </td>
                        {canWrite && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {editingId === role.id ? (
                                <>
                                  <button onClick={handleUpdate} className="text-ga-success hover:opacity-80" title="Save"><Save className="h-5 w-5" /></button>
                                  <button onClick={() => setEditingId(null)} className="text-ga-text-muted hover:text-ga-text-primary" title="Cancel"><X className="h-5 w-5" /></button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => handleEdit(role)} className="text-ga-info hover:opacity-80" title="Edit"><Edit className="h-5 w-5" /></button>
                                  {!role.is_system && (
                                    <button onClick={() => handleDelete(role.id, role.name)} className="text-ga-danger hover:opacity-80" title="Delete"><Trash2 className="h-5 w-5" /></button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {editingId && !showAddForm && canWrite && (
          <div className="mt-6 p-6 ga-card rounded-ga-md shadow-ga-soft border border-ga-border-subtle">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4">Edit Role: {formData.name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Role Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                  disabled={roles.find(r => r.id === editingId)?.is_system}
                  className="ga-input w-full rounded-ga-sm disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="2"
                  disabled={roles.find(r => r.id === editingId)?.is_system}
                  className="ga-input w-full rounded-ga-sm disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-3">Module Permissions *</label>
                <PermissionGrid
                  perms={editPermissions}
                  onChange={(m, t, c) => handlePermissionChange(m, t, c, true)}
                  disabled={roles.find(r => r.id === editingId)?.is_system}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Roles;
